import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Badge } from "../components/ui/Badge";
import { Switch } from "../components/ui/Switch";
import { couponService, Coupon } from "../services/coupons";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, Ticket, CheckCircle, Hash, X, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/AlertDialog";

export function CuponsPage() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState<{ visible: boolean; editingId?: number }>({ visible: false });
  const [deleteConfirm, setDeleteConfirm] = useState<{ visible: boolean; id?: number }>({ visible: false });

  const [formData, setFormData] = useState({
    code: "",
    type: "fixed" as "fixed" | "percent" | "free_shipping",
    value: "",
    min_order_value: "",
    quantity: "",
    init_at: "",
    finish_at: "",
    is_active: true,
  });

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await couponService.getAll();
      // Se paginado
      const data = response.data.data ? response.data.data : response.data;
      setCoupons(data);
    } catch (error) {
      toast.error("Erro ao carregar cupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const openAddModal = () => {
    setFormData({
      code: "",
      type: "fixed",
      value: "",
      min_order_value: "",
      quantity: "",
      init_at: "",
      finish_at: "",
      is_active: true,
    });
    setShowModal({ visible: true });
  };

  const isoToDatetimeLocal = (dateStr?: string | null) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  };

  const handleEdit = (coupon: Coupon) => {
    setFormData({
      code: coupon.code,
      type: coupon.type,
      value: String(coupon.value),
      min_order_value: coupon.min_order_value !== null && coupon.min_order_value !== undefined ? String(coupon.min_order_value) : "",
      quantity: coupon.quantity !== null && coupon.quantity !== undefined ? String(coupon.quantity) : "",
      init_at: isoToDatetimeLocal(coupon.init_at),
      finish_at: isoToDatetimeLocal(coupon.finish_at),
      is_active: coupon.is_active,
    });
    setShowModal({ visible: true, editingId: coupon.id });
  };

  const handleSave = async () => {
    if (!formData.code.trim()) {
      toast.error("O código do cupom é obrigatório.");
      return;
    }
    if (formData.type !== 'free_shipping' && (!formData.value || parseFloat(formData.value) <= 0)) {
      toast.error("Informe um valor de desconto válido maior que zero.");
      return;
    }

    const payload: Partial<Coupon> = {
      code: formData.code.trim().toUpperCase(),
      type: formData.type,
      value: formData.type === 'free_shipping' ? 0 : parseFloat(formData.value),
      min_order_value: formData.min_order_value ? parseFloat(formData.min_order_value) : null,
      quantity: formData.quantity ? parseInt(formData.quantity, 10) : null,
      init_at: formData.init_at ? formData.init_at : null,
      finish_at: formData.finish_at ? formData.finish_at : null,
      is_active: formData.is_active,
    };

    setSaving(true);
    try {
      if (showModal.editingId) {
        await couponService.update(showModal.editingId, payload);
        toast.success("Cupom atualizado com sucesso!");
      } else {
        await couponService.create(payload);
        toast.success("Cupom criado com sucesso!");
      }
      setShowModal({ visible: false });
      fetchCoupons();
    } catch (error: any) {
      toast.error("Erro ao salvar cupom", {
        description: error.response?.data?.message || "Verifique os dados e tente novamente."
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (coupon: Coupon) => {
    try {
      await couponService.update(coupon.id, {
        code: coupon.code,
        type: coupon.type,
        value: coupon.value,
        is_active: !coupon.is_active,
      });
      toast.success(`Cupom ${!coupon.is_active ? "ativado" : "desativado"}`);
      fetchCoupons();
    } catch (error) {
      toast.error("Erro ao alterar status do cupom.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.id) return;
    setDeleting(true);
    try {
      await couponService.delete(deleteConfirm.id);
      toast.success("Cupom excluído com sucesso!");
      fetchCoupons();
    } catch (error) {
      toast.error("Erro ao excluir cupom.");
    } finally {
      setDeleting(false);
      setDeleteConfirm({ visible: false });
    }
  };

  const filteredCoupons = coupons.filter(c =>
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter(c => c.is_active).length;
  const totalUses = coupons.reduce((sum, c) => sum + (c.used_count || 0), 0);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <Ticket className="h-6 w-6 text-[#D4C070]" />
            Cupons Promocionais
          </h1>
          <p className="text-sm text-gray-500">
            Gerencie os códigos de desconto disponíveis para os clientes no checkout.
          </p>
        </div>
        <Button onClick={openAddModal} className="bg-[#D4C070] hover:bg-[#c2b062] text-white flex items-center gap-2">
          <Plus className="h-4 w-4" /> Novo Cupom
        </Button>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total de Cupons</CardTitle>
            <Ticket className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{totalCoupons}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Cupons Ativos</CardTitle>
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{activeCoupons}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total de Utilizações</CardTitle>
            <Hash className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{totalUses}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Lista */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Carregando cupons...</div>
          ) : filteredCoupons.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Nenhum cupom encontrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-700 uppercase text-[11px] font-semibold">
                  <tr>
                    <th className="px-4 py-3">Código</th>
                    <th className="px-4 py-3">Tipo / Desconto</th>
                    <th className="px-4 py-3">Pedido Mínimo</th>
                    <th className="px-4 py-3">Usos / Limite</th>
                    <th className="px-4 py-3">Período de Validade</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCoupons.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 font-mono font-bold text-gray-900 uppercase">
                        {coupon.code}
                      </td>
                      <td className="px-4 py-3">
                        {coupon.type === "free_shipping" ? (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800 font-semibold">
                            Frete Grátis 🚚
                          </Badge>
                        ) : coupon.type === "percent" ? (
                          <Badge variant="secondary" className="bg-purple-100 text-purple-800 font-semibold">
                            {coupon.value}% OFF
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 font-semibold">
                            R$ {Number(coupon.value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} OFF
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {coupon.min_order_value && Number(coupon.min_order_value) > 0 ? (
                          `R$ ${Number(coupon.min_order_value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                        ) : (
                          <span className="text-gray-400">Sem mínimo</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="font-semibold">{coupon.used_count}</span>
                        {coupon.quantity !== null && coupon.quantity !== undefined ? (
                          <span className="text-gray-500"> / {coupon.quantity}</span>
                        ) : (
                          <span className="text-gray-400"> (Ilimitado)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div><span className="text-gray-400">De:</span> {formatDate(coupon.init_at)}</div>
                        <div><span className="text-gray-400">Até:</span> {formatDate(coupon.finish_at)}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center">
                          <Switch
                            checked={coupon.is_active}
                            onCheckedChange={() => handleToggleActive(coupon)}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(coupon)}
                            className="h-8 w-8 text-gray-600 hover:text-black"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirm({ visible: true, id: coupon.id })}
                            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criar / Editar Cupom */}
      {showModal.visible && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Ticket className="h-5 w-5 text-[#D4C070]" />
                {showModal.editingId ? "Editar Cupom" : "Novo Cupom Promocional"}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowModal({ visible: false })}
                className="h-8 w-8 text-gray-500"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div>
                <Label htmlFor="code" className="text-xs font-bold uppercase text-gray-700">
                  Código do Cupom *
                </Label>
                <Input
                  id="code"
                  placeholder="EX: DESCONTO10"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="mt-1 font-mono uppercase text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type" className="text-xs font-bold uppercase text-gray-700">
                    Tipo de Desconto *
                  </Label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'fixed' | 'percent' | 'free_shipping' })}
                    className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    <option value="fixed">Valor Fixo (R$)</option>
                    <option value="percent">Porcentagem (%)</option>
                    <option value="free_shipping">Frete Grátis 🚚</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="value" className="text-xs font-bold uppercase text-gray-700">
                    Valor do Desconto {formData.type === 'free_shipping' ? '' : '*'}
                  </Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    disabled={formData.type === 'free_shipping'}
                    placeholder={formData.type === 'free_shipping' ? 'Automático (Frete)' : formData.type === 'percent' ? 'Ex: 10' : 'Ex: 25.00'}
                    value={formData.type === 'free_shipping' ? '' : formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="mt-1 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {formData.type === 'free_shipping' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-start gap-2">
                  <span>ℹ️</span>
                  <div>
                    <strong>Frete Grátis:</strong> O desconto zera o valor da taxa de entrega calculada no carrinho do cliente. O valor é calculado dinamicamente e limitado ao custo real do frete.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_order_value" className="text-xs font-bold uppercase text-gray-700">
                    Pedido Mínimo (R$)
                  </Label>
                  <Input
                    id="min_order_value"
                    type="number"
                    step="0.01"
                    placeholder="Opcional"
                    value={formData.min_order_value}
                    onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })}
                    className="mt-1 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="quantity" className="text-xs font-bold uppercase text-gray-700">
                    Limite de Usos Total
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    placeholder="Vazio = Ilimitado"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="mt-1 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="init_at" className="text-xs font-bold uppercase text-gray-700">
                    Data de Início
                  </Label>
                  <Input
                    id="init_at"
                    type="datetime-local"
                    value={formData.init_at}
                    onChange={(e) => setFormData({ ...formData, init_at: e.target.value })}
                    className="mt-1 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="finish_at" className="text-xs font-bold uppercase text-gray-700">
                    Data de Término
                  </Label>
                  <Input
                    id="finish_at"
                    type="datetime-local"
                    value={formData.finish_at}
                    onChange={(e) => setFormData({ ...formData, finish_at: e.target.value })}
                    className="mt-1 text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Label htmlFor="is_active" className="text-xs font-bold uppercase text-gray-700 cursor-pointer">
                  Cupom Ativo
                </Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal({ visible: false })} disabled={saving}>
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#D4C070] hover:bg-[#c2b062] text-white flex items-center gap-2 min-w-[120px] justify-center"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  "Salvar Cupom"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AlertDialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteConfirm.visible} onOpenChange={(open) => !open && setDeleteConfirm({ visible: false })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Cupom Promocional?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não poderá ser desfeita. O cupom deixará de ficar disponível para aplicação no checkout.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Excluindo...</span>
                </>
              ) : (
                "Excluir"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
